import { computed, ref } from 'vue';

export type LettrExportPhase = 'exporting' | 'preparing';

export interface ILettrExportProgress {
    done: number;
    total: number;
    phase: LettrExportPhase;
}

/**
 * Module level so the bar survives the selection being cleared: the run starts
 * by cancelling the multiple-select, which unmounts everything hanging off it.
 */
const progress = ref<ILettrExportProgress | null>(null);

export const useLettrExportProgress = () => {
    const start = (total: number) => {
        progress.value = { done: 0, total, phase: 'exporting' };
    };

    const update = (next: ILettrExportProgress) => {
        // Events can arrive out of order, and a bar that goes backwards reads
        // as a bug. Within a phase progress only ever grows; a new phase
        // restarts the count because it is measuring something else.
        if (
            progress.value !== null &&
            progress.value.phase === next.phase &&
            next.done < progress.value.done
        ) {
            return;
        }

        progress.value = next;
    };

    const finish = () => {
        progress.value = null;
    };

    const label = computed(() => {
        if (progress.value === null) {
            return '';
        }

        return progress.value.phase === 'exporting'
            ? `Sending to Lettr — ${progress.value.done} of ${progress.value.total}`
            : `Lettr is preparing them — ${progress.value.done} of ${progress.value.total} done`;
    });

    const percentage = computed(() => {
        if (progress.value === null || progress.value.total === 0) {
            return 0;
        }

        return Math.min(
            100,
            Math.round((progress.value.done / progress.value.total) * 100)
        );
    });

    return { progress, start, update, finish, label, percentage };
};

export default useLettrExportProgress;
